import {allocationStatusForRow} from './attention-lens'
import {type BoardLane, classifyBoardLane} from './board-lanes'
import {buildCommitmentJobDisplay, type CommitmentJobDisplay} from './commitment-job-display'
import type {TimelineJobRow} from './job-rows'
import {deriveOperationalState, type OperationalState} from './operational-state'
import type {OperationalSignalTier} from './radio-status'
import {resolveRadioStatus} from './radio-status'
import {terminalScheduleLabelFromContext} from './schedule-language'
import {resolveNowHour} from './schedule-urgency'
import type {DispatchTimelineData, ViewRange} from './types'

export type DrawerBoardContext = {
    display: CommitmentJobDisplay
    operational: OperationalState
    lane: BoardLane
    radioStatus: {line: string; tier: OperationalSignalTier}
    scheduleAnchor: string
    scheduleDetail?: string
    showAssignControls: boolean
}

export function buildIdentityScheduleLabel(
    context: DrawerBoardContext,
    absoluteScheduleLabel: string | undefined,
): string {
    if (absoluteScheduleLabel) {
        return absoluteScheduleLabel
    }

    return [context.scheduleAnchor, context.scheduleDetail].filter(Boolean).join(' · ')
}

/** Board-derived operational context for the dispatch job drawer. */
export function buildDrawerBoardContext(
    row: TimelineJobRow,
    data: DispatchTimelineData,
    viewRange: ViewRange,
): DrawerBoardContext {
    const display = buildCommitmentJobDisplay(row)
    const allocationStatus = allocationStatusForRow(row)
    const operational = deriveOperationalState(row, allocationStatus)
    const lane = classifyBoardLane(row, operational)

    const visibleDays = data.scheduleDays ?? data.visibleDays ?? [data.scheduleDate]
    const nowHour = resolveNowHour()
    const viewDayOriginOffset = data.viewDayOriginOffset ?? 0
    const viewDayOffset = (row.span?.dayOffset ?? 0) - viewDayOriginOffset

    const radioStatus = resolveRadioStatus(display, operational, {
        warnings: row.warnings,
        unconfirmedRentalCount: row.unconfirmedRentalCount,
    })

    const schedule = terminalScheduleLabelFromContext(
        row,
        display,
        {visibleDays, viewRange, nowHour},
        {viewDayOffset},
    )

    const canAssignOnBoard = row.status === 'unassigned'
    const showAssignControls = canAssignOnBoard

    return {
        display,
        operational,
        lane,
        radioStatus,
        scheduleAnchor: schedule.anchor,
        scheduleDetail: schedule.duration,
        showAssignControls,
    }
}
